// microblog-lms/components/organisms/LectureEditForm/lectureFormMachine.ts
import { assign, fromPromise, setup } from 'xstate';
import { v4 as uuidv4 } from 'uuid';
const sanitizeLessonTitle = (value)=>{
    const trimmed = (value ?? '').trim();
    return trimmed.length > 0 ? trimmed : '제목 없음';
};
// setup API 사용
export const lectureFormMachine = setup({
    types: {
        context: {},
        events: {},
        input: {}
    },
    actors: {
        parseMarkdownActor: fromPromise(async ({ input })=>{
            if (!input.parseFunction) throw new Error("parseFunction is not provided to parseMarkdownActor");
            return input.parseFunction(input.markdownToParse, input.stableKey);
        }),
        saveLessonActor: fromPromise(async ({ input: actorInput })=>{
            if (!actorInput.updateFunction) throw new Error("updateFunction is not provided to saveLessonActor");
            const sanitizedTitle = sanitizeLessonTitle(actorInput.lessonDataToSave.title);
            const payload = {
                ...actorInput.lessonDataToSave,
                title: sanitizedTitle
            };
            actorInput.updateFunction(actorInput.sectionId, actorInput.lessonId, payload);
            const updatedLessonForMachine = {
                id: actorInput.lessonId,
                title: sanitizedTitle,
                markdown: actorInput.lessonDataToSave.markdown
            };
            return updatedLessonForMachine;
        })
    }
}).createMachine({
    id: 'lectureForm',
    predictableActionArguments: true,
    context: ({ input })=>({
            selectedLessonInput: input.initialSelectedLesson || null,
            currentLessonData: input.initialLessonData || null,
            formData: {
                title: sanitizeLessonTitle(input.initialLessonData?.title),
                markdown: input.initialLessonData?.markdown ?? '',
                blocks: []
            },
            editorMode: 'markdown',
            isDirty: false,
            errorMessage: null,
            courseId: input.courseId,
            stableKeyForBlockIds: input.stableKeyForBlockIds,
            // setup input에서 받은 함수를 context에 저장
            doUpdateLessonInStore: input.doUpdateLessonInStore,
            doParseMarkdown: input.doParseMarkdown
        }),
    initial: 'idle',
    states: {
        idle: {
            on: {
                INIT_OR_UPDATE_LESSON: {
                    target: 'initializing',
                    actions: assign({
                        selectedLessonInput: ({ event })=>event.lessonInput,
                        courseId: ({ event })=>event.courseId,
                        currentLessonData: ({ event })=>event.lessonDataFromStore || null,
                        stableKeyForBlockIds: ({ event })=>event.lessonInput?.lessonId || uuidv4(),
                        formData: ({ context, event })=>{
                            const newLessonId = event.lessonInput?.lessonId;
                            const oldLessonId = context.selectedLessonInput?.lessonId;
                            const currentFormData = context.formData;
                            if (newLessonId !== oldLessonId) {
                                return {
                                    title: sanitizeLessonTitle(event.lessonDataFromStore?.title),
                                    markdown: event.lessonDataFromStore?.markdown || '',
                                    blocks: []
                                };
                            } else if (event.lessonDataFromStore) {
                                return {
                                    title: event.lessonDataFromStore.title !== currentFormData.title ? sanitizeLessonTitle(event.lessonDataFromStore.title) : currentFormData.title,
                                    markdown: event.lessonDataFromStore.markdown !== currentFormData.markdown ? event.lessonDataFromStore.markdown || '' : currentFormData.markdown,
                                    blocks: currentFormData.blocks
                                };
                            }
                            return currentFormData;
                        },
                        isDirty: false,
                        editorMode: 'markdown',
                        errorMessage: null
                    })
                }
            }
        },
        initializing: {
            always: [
                {
                    target: 'parsingMarkdown',
                    guard: ({ context })=>!!context.formData.markdown && context.formData.markdown.trim() !== '',
                    actions: assign({
                        errorMessage: null
                    })
                },
                {
                    target: 'editing',
                    actions: assign({
                        formData: ({ context })=>({
                                ...context.formData,
                                blocks: [
                                    {
                                        id: uuidv4(),
                                        type: 'markdown',
                                        content: '',
                                        isOpen: true,
                                        editorHeight: 200
                                    }
                                ]
                            }),
                        errorMessage: null
                    })
                }
            ]
        },
        parsingMarkdown: {
            invoke: {
                id: 'parseMarkdownActor',
                src: 'parseMarkdownActor',
                input: ({ context })=>({
                        markdownToParse: context.formData.markdown,
                        stableKey: context.stableKeyForBlockIds,
                        parseFunction: context.doParseMarkdown
                    }),
                onDone: {
                    target: 'editing',
                    actions: assign({
                        formData: ({ context, event })=>({
                                ...context.formData,
                                blocks: event.output
                            })
                    })
                },
                onError: {
                    target: 'editing',
                    actions: assign({
                        errorMessage: '마크다운 파싱 중 오류가 발생했습니다. 기본 블록으로 시작합니다.',
                        formData: ({ context })=>({
                                ...context.formData,
                                blocks: [
                                    {
                                        id: uuidv4(),
                                        type: 'markdown',
                                        content: context.formData.markdown || '',
                                        isOpen: true,
                                        editorHeight: 200
                                    }
                                ]
                            })
                    })
                }
            }
        },
        editing: {
            on: {
                UPDATE_TITLE: {
                    actions: assign({
                        formData: ({ context, event })=>({
                                ...context.formData,
                                title: sanitizeLessonTitle(event.title)
                            }),
                        isDirty: ({ context })=>true
                    })
                },
                UPDATE_MARKDOWN: {
                    actions: assign({
                        formData: ({ context, event })=>({
                                ...context.formData,
                                markdown: event.markdown
                            })
                    })
                },
                UPDATE_BLOCKS: {
                    actions: assign({
                        formData: ({ context, event })=>({
                                ...context.formData,
                                blocks: event.blocks
                            })
                    })
                },
                ADD_BLOCK: {
                    actions: assign({
                        formData: ({ context, event })=>{
                            const currentBlocks = context.formData.blocks || [];
                            const newContent = event.content || event.getDefaultContent(event.blockType);
                            const newBlock = {
                                id: uuidv4(),
                                type: event.blockType,
                                content: newContent,
                                metadata: event.metadata || {},
                                isOpen: true,
                                editorHeight: 200
                            };
                            const newBlocks = [
                                ...currentBlocks.slice(0, event.index),
                                newBlock,
                                ...currentBlocks.slice(event.index)
                            ];
                            return {
                                ...context.formData,
                                blocks: newBlocks
                            };
                        }
                    })
                },
                SET_DIRTY_STATUS: {
                    actions: assign({
                        isDirty: ({ event })=>event.isDirty
                    })
                },
                TOGGLE_EDITOR_MODE: [
                    {
                        guard: ({ context })=>context.editorMode === 'block',
                        actions: assign({
                            editorMode: 'markdown',
                            formData: ({ context })=>({
                                    ...context.formData,
                                    markdown: (context.formData.blocks || []).map((b)=>b.content).join('\n\n')
                                })
                        })
                    },
                    {
                        guard: ({ context })=>context.editorMode === 'markdown',
                        target: 'parsingMarkdown',
                        actions: assign({
                            editorMode: 'block'
                        })
                    }
                ],
                SAVE_LESSON: {
                    target: 'saving',
                    guard: ({ context })=>context.isDirty && !!context.selectedLessonInput
                },
                INIT_OR_UPDATE_LESSON: {
                    target: 'initializing',
                    actions: assign({
                        selectedLessonInput: ({ event })=>event.lessonInput,
                        courseId: ({ event })=>event.courseId,
                        currentLessonData: ({ event })=>event.lessonDataFromStore || null,
                        stableKeyForBlockIds: ({ event })=>event.lessonInput?.lessonId || uuidv4(),
                        formData: ({ context, event })=>{
                            const newLessonId = event.lessonInput?.lessonId;
                            const oldLessonId = context.selectedLessonInput?.lessonId;
                            const currentFormData = context.formData;
                            if (newLessonId !== oldLessonId) {
                                return {
                                    title: sanitizeLessonTitle(event.lessonDataFromStore?.title),
                                    markdown: event.lessonDataFromStore?.markdown || '',
                                    blocks: []
                                };
                            } else if (event.lessonDataFromStore) {
                                return {
                                    title: event.lessonDataFromStore.title !== currentFormData.title ? sanitizeLessonTitle(event.lessonDataFromStore.title) : currentFormData.title,
                                    markdown: event.lessonDataFromStore.markdown !== currentFormData.markdown ? event.lessonDataFromStore.markdown || '' : currentFormData.markdown,
                                    blocks: currentFormData.blocks
                                };
                            }
                            return currentFormData;
                        },
                        isDirty: false,
                        editorMode: 'markdown',
                        errorMessage: null
                    })
                }
            }
        },
        saving: {
            invoke: {
                id: 'saveLessonActor',
                src: 'saveLessonActor',
                input: ({ context })=>{
                    const markdownToSave = context.editorMode === 'markdown' ? context.formData.markdown : (context.formData.blocks || []).map((b)=>b.content).join('\n\n');
                    return {
                        sectionId: context.selectedLessonInput.sectionId,
                        lessonId: context.selectedLessonInput.lessonId,
                        lessonDataToSave: {
                            title: sanitizeLessonTitle(context.formData.title),
                            markdown: markdownToSave
                        },
                        updateFunction: context.doUpdateLessonInStore
                    };
                },
                onDone: {
                    target: 'editing',
                    actions: assign({
                        isDirty: false,
                        errorMessage: null,
                        currentLessonData: ({ event })=>event.output
                    })
                },
                onError: {
                    target: 'editing',
                    actions: assign({
                        errorMessage: '저장 중 오류가 발생했습니다. 다시 시도해주세요.'
                    })
                }
            }
        }
    }
});
